import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContactSections } from './components/ContactSections';
import { InvitationTools } from './components/InvitationTools';
import { OfferCard } from './components/OfferCard';
import { ProductInfo } from './components/ProductInfo';
import { deriveContactInvite } from './contacts/contact';
import { createPreparedParcel, restorePreparedParcel } from './contacts/parcels';
import { ContactProtocol, type IncomingContactProposal } from './contacts/protocol';
import { GameProtocol } from './games/protocol';
import type { GameLoungeSnapshot } from './games/types';
import { capabilityDescription, detectDeviceCapabilities, type DeviceCapabilities } from './lib/capabilities';
import { configurationProblems } from './lib/config';
import { connectionSummary } from './lib/connection';
import { APP_NAME, APP_VERSION } from './lib/constants';
import { copyDiagnostics } from './lib/diagnostics';
import { formatBytes, formatDuration, friendlyError } from './lib/format';
import { createInvite, inviteUrl, parseInvite, type Invite } from './lib/invite';
import { activatePwaUpdate, isStandaloneApp, PWA_UPDATE_EVENT, type BeforeInstallPromptEvent } from './lib/pwa';
import { buildTransferReceipt } from './lib/receipt';
import {
  clearTransferHistory,
  deleteContact,
  deletePreparedParcel,
  deleteSession,
  deleteTransfer,
  listContacts,
  listPreparedParcels,
  listSessions,
  listTransferHistory,
  listTransfers,
  requestPersistentLocalStorage,
  saveContact,
  savePreparedParcel,
  saveSession,
  saveTransferHistory,
  type StoredContact,
  type StoredPreparedParcel,
  type StoredSession,
  type StoredTransferHistory,
} from './lib/storage';
import { SignalingSession, type JoinRequest } from './signaling/signaling';
import { ensureWritePermission, mergeSelections, removeTopLevel, selectionFromDrop, selectionFromFileList, selectionFromHandles, testDestinationWrite, type SourceSelection } from './transfer/files';
import { TransferManager } from './transfer/manager';
import type { ConflictPolicy, ConflictRequest, IncomingOffer, LocalSource, QueueSnapshot, SpeedTestResult, TransferCapabilities, TransferProgress } from './transfer/types';
import { PeerSession, type PeerDiagnostics } from './webrtc/peer';

const GameLounge = lazy(() => import('./components/GameLounge'));

type Stage = 'idle' | 'hosting' | 'join' | 'waiting' | 'contacting' | 'connecting' | 'connected' | 'error';
type PreparedHostOfferStatus = 'idle' | 'queued' | 'offered';
const COFFEE_URL = 'https://buymeacoffee.com/djpokis';

const emptyDiagnostics: PeerDiagnostics = {
  connectionState: 'new',
  iceState: 'new',
  signalingState: 'stable',
};

const emptyGameLounge: GameLoungeSnapshot = { peerAvailable: false, proposals: [] };

function App() {
  const [stage, setStage] = useState<Stage>(() => parseInvite() ? 'join' : 'idle');
  const [activeInvite, setActiveInvite] = useState<Invite | null>(() => parseInvite());
  const [inviteLink, setInviteLink] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [joinRequests, setJoinRequests] = useState<JoinRequest[]>([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [diagnostics, setDiagnostics] = useState<PeerDiagnostics>(emptyDiagnostics);
  const [selectedSources, setSelectedSources] = useState<LocalSource[]>([]);
  const [selectedDirectories, setSelectedDirectories] = useState<string[]>([]);
  const [offers, setOffers] = useState<IncomingOffer[]>([]);
  const [progress, setProgress] = useState<TransferProgress[]>([]);
  const [remoteCapabilities, setRemoteCapabilities] = useState<TransferCapabilities | null>(null);
  const [recentSessions, setRecentSessions] = useState<StoredSession[]>([]);
  const [contacts, setContacts] = useState<StoredContact[]>([]);
  const [preparedParcels, setPreparedParcels] = useState<StoredPreparedParcel[]>([]);
  const [transferHistory, setTransferHistory] = useState<StoredTransferHistory[]>([]);
  const [activeContact, setActiveContact] = useState<StoredContact | null>(null);
  const [preparingContact, setPreparingContact] = useState<StoredContact | null>(null);
  const [incomingContactProposal, setIncomingContactProposal] = useState<IncomingContactProposal | null>(null);
  const [contactNameDraft, setContactNameDraft] = useState('');
  const [saveContactOpen, setSaveContactOpen] = useState(false);
  const [conflictRequest, setConflictRequest] = useState<ConflictRequest | null>(null);
  const [queue, setQueue] = useState<QueueSnapshot>({ batchIds: [] });
  const [speedTest, setSpeedTest] = useState<SpeedTestResult | null>(null);
  const [speedTesting, setSpeedTesting] = useState(false);
  const [preparedHostOfferStatus, setPreparedHostOfferStatus] = useState<PreparedHostOfferStatus>('idle');
  const [gameLounge, setGameLounge] = useState<GameLoungeSnapshot>(emptyGameLounge);
  const [helpOpen, setHelpOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [standalone, setStandalone] = useState(() => isStandaloneApp());
  const [completionAlerts, setCompletionAlerts] = useState(() => {
    try { return localStorage.getItem('pairferry:completion-alerts') === 'on'; } catch { return false; }
  });
  const signalingRef = useRef<SignalingSession | undefined>(undefined);
  const peerRef = useRef<PeerSession | undefined>(undefined);
  const transferRef = useRef<TransferManager | undefined>(undefined);
  const contactProtocolRef = useRef<ContactProtocol | undefined>(undefined);
  const gameProtocolRef = useRef<GameProtocol | undefined>(undefined);
  const activeContactRef = useRef<StoredContact | null>(null);
  const preparedForMeetingRef = useRef<Array<{ parcel: StoredPreparedParcel; sources: LocalSource[] }>>([]);
  const preparedBatchRef = useRef(new Map<string, string>());
  const roleRef = useRef<'host' | 'guest'>('host');
  const audioContextRef = useRef<AudioContext | undefined>(undefined);
  const announcedCompletionsRef = useRef(new Set<string>());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const displayNameInputRef = useRef<HTMLInputElement | null>(null);
  const backgroundPausedRef = useRef<string | undefined>(undefined);
  const selectedSourcesRef = useRef<LocalSource[]>([]);
  const selectedDirectoriesRef = useRef<string[]>([]);
  const preparedHostOfferStatusRef = useRef<PreparedHostOfferStatus>('idle');
  const preparedHostBatchRef = useRef<string | undefined>(undefined);
  const preparationWakeLockRef = useRef<WakeLockSentinel | undefined>(undefined);
  const configProblems = useMemo(() => configurationProblems(), []);
  const capabilities = useMemo(() => detectDeviceCapabilities(), []);
  const localTransferCapabilities = useMemo<TransferCapabilities>(() => ({
    receiveFiles: capabilities.canReceive,
    receiveFolders: capabilities.canReceive,
    resumable: capabilities.canResumeOutgoing,
    mobile: capabilities.mobile,
  }), [capabilities]);
  const hasActiveTransfer = progress.some((item) => item.state === 'active' || item.state === 'paused');
  selectedSourcesRef.current = selectedSources;
  selectedDirectoriesRef.current = selectedDirectories;

  useEffect(() => {
    void refreshLocalData();
    return () => {
      void preparationWakeLockRef.current?.release().catch(() => undefined);
      gameProtocolRef.current?.close();
      contactProtocolRef.current?.close();
      peerRef.current?.close();
      void signalingRef.current?.close();
    };
  }, []);

  useEffect(() => {
    if (stage === 'join') displayNameInputRef.current?.focus();
  }, [stage]);

  useEffect(() => {
    const captureInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const installed = () => {
      setStandalone(true);
      setInstallPrompt(null);
      setInstallHelpOpen(false);
    };
    const updateReady = () => setUpdateAvailable(true);
    window.addEventListener('beforeinstallprompt', captureInstallPrompt);
    window.addEventListener('appinstalled', installed);
    window.addEventListener(PWA_UPDATE_EVENT, updateReady);
    return () => {
      window.removeEventListener('beforeinstallprompt', captureInstallPrompt);
      window.removeEventListener('appinstalled', installed);
      window.removeEventListener(PWA_UPDATE_EVENT, updateReady);
    };
  }, []);

  useEffect(() => {
    const safeToReload = stage === 'idle' || stage === 'join' || stage === 'error';
    if (!updateAvailable || hasActiveTransfer || !safeToReload) return;
    const timeout = window.setTimeout(() => {
      if (!activatePwaUpdate()) setUpdateAvailable(false);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [hasActiveTransfer, stage, updateAvailable]);

  useEffect(() => {
    if (!hasActiveTransfer) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = true;
    };
    const saveBeforeLeaving = () => void transferRef.current?.checkpointNow();
    const handleVisibility = () => {
      const manager = transferRef.current;
      if (!manager) return;
      if (document.visibilityState === 'hidden') {
        void manager.checkpointNow();
        const active = progress.find((item) => item.state === 'active');
        if (active) {
          backgroundPausedRef.current = active.batchId;
          manager.pause(active.batchId);
        }
        return;
      }
      const backgroundPaused = backgroundPausedRef.current;
      if (backgroundPaused) {
        backgroundPausedRef.current = undefined;
        manager.resume(backgroundPaused);
        setMessage('Transfer resumed after returning to the app.');
      }
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    window.addEventListener('pagehide', saveBeforeLeaving);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('beforeunload', warnBeforeLeaving);
      window.removeEventListener('pagehide', saveBeforeLeaving);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [hasActiveTransfer, progress]);

  async function refreshLocalData() {
    let sessions: StoredSession[] = [];
    let transfers: Awaited<ReturnType<typeof listTransfers>> = [];
    let storedContacts: StoredContact[] = [];
    let parcels: StoredPreparedParcel[] = [];
    let historyItems: StoredTransferHistory[] = [];
    try {
      [sessions, transfers, storedContacts, parcels, historyItems] = await Promise.all([
        listSessions(), listTransfers(), listContacts(), listPreparedParcels(), listTransferHistory(),
      ]);
    } catch {
      // The app remains usable for a one-time transfer when local storage is unavailable.
    }
    const unfinishedRooms = new Set(transfers.filter((item) => item.state !== 'complete').map((item) => item.roomId));
    setRecentSessions(sessions.filter((session) => unfinishedRooms.has(session.roomId)).sort((a, b) => b.updatedAt - a.updatedAt));
    setContacts(storedContacts.sort((a, b) => (b.lastMetAt ?? b.createdAt) - (a.lastMetAt ?? a.createdAt)));
    setPreparedParcels(parcels.sort((a, b) => b.createdAt - a.createdAt));
    setTransferHistory(historyItems.sort((a, b) => b.completedAt - a.completedAt));
  }

  function fail(value: unknown) {
    if (preparedHostOfferStatusRef.current !== 'idle') {
      updatePreparedHostOfferStatus('idle');
      preparedHostBatchRef.current = undefined;
      void releasePreparationWakeLock();
    }
    setError(friendlyError(value));
    setStage('error');
  }

  function updatePreparedHostOfferStatus(status: PreparedHostOfferStatus) {
    preparedHostOfferStatusRef.current = status;
    setPreparedHostOfferStatus(status);
  }

  async function holdPreparationWakeLock() {
    if (!navigator.wakeLock || document.visibilityState !== 'visible' || (preparationWakeLockRef.current && !preparationWakeLockRef.current.released)) return;
    const wakeLock = await navigator.wakeLock.request('screen').catch(() => undefined);
    if (!wakeLock) return;
    preparationWakeLockRef.current = wakeLock;
    wakeLock.addEventListener('release', () => {
      if (preparationWakeLockRef.current === wakeLock) preparationWakeLockRef.current = undefined;
    }, { once: true });
  }

  async function releasePreparationWakeLock() {
    await preparationWakeLockRef.current?.release().catch(() => undefined);
    preparationWakeLockRef.current = undefined;
  }

  async function queuePreparedHostOffer() {
    if (!selectedSourcesRef.current.length && !selectedDirectoriesRef.current.length) return;
    updatePreparedHostOfferStatus('queued');
    await requestPersistentLocalStorage().catch(() => false);
    await holdPreparationWakeLock();
    setMessage('Files queued. After you approve the recipient, PairFerry will offer them automatically over the direct connection.');
  }

  function selectionChanged() {
    if (preparedHostOfferStatusRef.current === 'idle') return;
    updatePreparedHostOfferStatus('idle');
    preparedHostBatchRef.current = undefined;
    void releasePreparationWakeLock();
  }

  function clearSelection() {
    selectedSourcesRef.current = [];
    selectedDirectoriesRef.current = [];
    setSelectedSources([]);
    setSelectedDirectories([]);
    selectionChanged();
  }

  async function startHost(invite = createInvite()): Promise<boolean> {
    try {
      if (configProblems.length) throw new Error('Firebase is not configured yet. Follow docs/FIREBASE_SETUP.md first.');
      activeContactRef.current = null;
      setActiveContact(null);
      preparedForMeetingRef.current = [];
      roleRef.current = 'host';
      setActiveInvite(invite);
      setInviteLink(inviteUrl(invite));
      setStage('hosting');
      setMessage('Creating a private transfer room…');
      const signaling = new SignalingSession(invite, 'host');
      signalingRef.current = signaling;
      await signaling.initialize();
      await signaling.createRoom((request) => setJoinRequests((current) => current.some((item) => item.uid === request.uid) ? current : [...current, request]));
      await saveSession({ id: invite.roomId, roomId: invite.roomId, secret: invite.secret, role: 'host', createdAt: Date.now(), updatedAt: Date.now() });
      setMessage('Share the private link, then keep this page open.');
      return true;
    } catch (value) {
      fail(value);
      return false;
    }
  }

  async function meetContact(contact: StoredContact): Promise<void> {
    try {
      if (configProblems.length) throw new Error('Firebase is not configured yet. Follow docs/FIREBASE_SETUP.md first.');
      if (!capabilities.canConnect) throw new Error('This browser cannot establish a WebRTC connection.');
      if (contact.oneTimeReceive && contact.oneTimeReceive.expiresAt <= Date.now()) {
        contact = { ...contact, oneTimeReceive: undefined, updatedAt: Date.now() };
        await saveContact(contact);
      }
      const prepared: Array<{ parcel: StoredPreparedParcel; sources: LocalSource[] }> = [];
      for (const parcel of preparedParcels.filter((item) => item.contactId === contact.id)) {
        prepared.push({ parcel, sources: await restorePreparedParcel(parcel) });
      }
      preparedForMeetingRef.current = prepared;
      activeContactRef.current = contact;
      setActiveContact(contact);
      roleRef.current = contact.role;
      const invite = await deriveContactInvite(contact);
      setActiveInvite(invite);
      setInviteLink('');
      setStage('contacting');
      setMessage(`Waiting for ${contact.name}. Both people must click the saved contact.`);
      const signaling = new SignalingSession(invite, contact.role);
      signalingRef.current = signaling;
      await signaling.initialize();
      await saveSession({ id: invite.roomId, roomId: invite.roomId, secret: invite.secret, role: contact.role, createdAt: Date.now(), updatedAt: Date.now() });
      if (contact.role === 'host') {
        let admitted = false;
        await signaling.createRoom((request) => {
          if (admitted) return;
          admitted = true;
          setStage('connecting');
          setMessage(`Found ${contact.name}. Verifying the saved contact…`);
          void signaling.approveGuest(request.uid)
            .then(() => connectPeer('host', signaling, request.uid))
            .catch(fail);
        });
      } else {
        await signaling.requestToJoin(contact.name, (meta) => {
          setStage('connecting');
          setMessage(`Found ${contact.name}. Verifying the saved contact…`);
          void connectPeer('guest', signaling, meta.hostUid).catch(fail);
        }, 10 * 60_000);
      }
    } catch (value) {
      fail(value);
    }
  }

  async function regenerateInvitation() {
    const oldInvite = activeInvite;
    setMessage('Revoking the previous invitation…');
    await signalingRef.current?.close();
    signalingRef.current = undefined;
    if (oldInvite) await deleteSession(oldInvite.roomId).catch(() => undefined);
    setJoinRequests([]);
    setCopied(false);
    if (await startHost(createInvite())) setMessage('The previous link is revoked. Share this new private link.');
  }

  async function approve(request: JoinRequest) {
    try {
      const signaling = signalingRef.current;
      if (!signaling) return;
      setJoinRequests([]);
      setStage('connecting');
      setMessage('Establishing a direct connection…');
      await signaling.approveGuest(request.uid);
      await connectPeer('host', signaling, request.uid);
    } catch (value) {
      fail(value);
    }
  }

  async function joinRoom() {
    const invite = activeInvite ?? parseInvite();
    if (!invite) return fail(new Error('This invitation link is incomplete or invalid.'));
    try {
      if (configProblems.length) throw new Error('This deployment is not configured yet.');
      roleRef.current = 'guest';
      setStage('waiting');
      setMessage('Waiting for the sender to approve you…');
      const signaling = new SignalingSession(invite, 'guest');
      signalingRef.current = signaling;
      await signaling.initialize();
      await saveSession({ id: invite.roomId, roomId: invite.roomId, secret: invite.secret, role: 'guest', createdAt: Date.now(), updatedAt: Date.now() });
      await signaling.requestToJoin(displayName || 'Other person', (meta) => {
        setStage('connecting');
        setMessage('Approved. Establishing a direct connection…');
        void connectPeer('guest', signaling, meta.hostUid).catch(fail);
      });
    } catch (value) {
      fail(value);
    }
  }

  async function connectPeer(role: 'host' | 'guest', signaling: SignalingSession, remoteUid: string) {
    const peer = new PeerSession(role, signaling, remoteUid, {
      onReady: () => void directConnectionReady(peer, signaling, role),
      onState: setDiagnostics,
      onError: fail,
    });
    peerRef.current = peer;
    await peer.start();
  }

  async function directConnectionReady(peer: PeerSession, signaling: SignalingSession, role: 'host' | 'guest') {
    try {
      gameProtocolRef.current?.close();
      contactProtocolRef.current?.close();
      const contactProtocol = new ContactProtocol(peer, signaling.invite.roomId, role, {
        onProposal: (proposal) => {
          setIncomingContactProposal(proposal);
          setContactNameDraft('');
        },
        onSaved: (contact) => {
          setMessage(`${contact.name} was saved as a trusted contact in this browser.`);
          void requestPersistentLocalStorage();
          void refreshLocalData();
        },
        onDeclined: () => setMessage('The other person declined the trusted-contact request.'),
      });
      contactProtocolRef.current = contactProtocol;
      const contact = activeContactRef.current;
      if (contact) {
        await contactProtocol.authenticate(contact);
        const updated = { ...contact, lastMetAt: Date.now(), updatedAt: Date.now() };
        await saveContact(updated);
        activeContactRef.current = updated;
        setActiveContact(updated);
      }
      await signaling.finishPairing();
      const gameProtocol = new GameProtocol(peer, role, {
        onChange: setGameLounge,
        onNotice: setMessage,
      });
      gameProtocolRef.current = gameProtocol;
      let resolvePeerReady: (capabilities: TransferCapabilities) => void = () => undefined;
      const peerReady = new Promise<TransferCapabilities>((resolve) => { resolvePeerReady = resolve; });
      const manager = new TransferManager(peer, signaling.invite.roomId, role, {
        onOffer: (offer) => {
          setOffers((current) => [...current.filter((item) => item.id !== offer.id), offer]);
          if (offer.status === 'offered' && !capabilities.canReceive) {
            setMessage('This device is in send-only mode and cannot accept incoming files.');
          }
          const trusted = activeContactRef.current;
          if (offer.status === 'offered' && trusted?.oneTimeReceive && trusted.oneTimeReceive.expiresAt > Date.now()) {
            const authorization = trusted.oneTimeReceive;
            const consumed = { ...trusted, oneTimeReceive: undefined, updatedAt: Date.now() };
            activeContactRef.current = consumed;
            setActiveContact(consumed);
            void saveContact(consumed)
              .then(() => manager.accept(offer.id, authorization.destinationHandle, { conflictPolicy: authorization.conflictPolicy }))
              .then(() => {
                setMessage(`Trusted one-time receive from ${trusted.name} started automatically.`);
                void refreshLocalData();
              })
              .catch((value) => setMessage(`Automatic receive could not start: ${friendlyError(value)} Choose a destination manually.`));
          }
        },
        onProgress: (item) => {
          setProgress((current) => [...current.filter((value) => value.batchId !== item.batchId), item]);
          if (
            preparedHostBatchRef.current === item.batchId
            && (item.state === 'active' || item.state === 'failed' || item.state === 'complete' || item.state === 'cancelled')
          ) {
            preparedHostBatchRef.current = undefined;
            updatePreparedHostOfferStatus('idle');
            void releasePreparationWakeLock();
          }
          const parcelId = preparedBatchRef.current.get(item.batchId);
          if (item.state === 'complete' && parcelId) {
            preparedBatchRef.current.delete(item.batchId);
            void deletePreparedParcel(parcelId).then(refreshLocalData);
          }
        },
        onNotice: setMessage,
        onPeerCapabilities: (peerCapabilities) => {
          setRemoteCapabilities(peerCapabilities);
          resolvePeerReady(peerCapabilities);
        },
        onQueueChange: setQueue,
        onConflict: setConflictRequest,
      }, localTransferCapabilities);
      transferRef.current = manager;
      setStage('connected');
      setMessage(contact ? `Trusted contact verified: ${contact.name}. Direct peer-to-peer connection is open.` : 'Direct peer-to-peer connection — files will not be uploaded anywhere.');
      const peerCapabilities = await Promise.race([
        peerReady,
        new Promise<never>((_, reject) => window.setTimeout(() => reject(new Error('The other browser did not finish the transfer handshake.')), 12_000)),
      ]);
      if (role === 'host' && preparedHostOfferStatusRef.current === 'queued') {
        const sources = selectedSourcesRef.current;
        const directories = selectedDirectoriesRef.current;
        if (!peerCapabilities.receiveFiles) {
          updatePreparedHostOfferStatus('idle');
          void releasePreparationWakeLock();
          setMessage('Connected, but the recipient’s browser is send-only. The prepared files remain selected so you can try another receiving device.');
        } else if (directories.length && !peerCapabilities.receiveFolders) {
          updatePreparedHostOfferStatus('idle');
          void releasePreparationWakeLock();
          setMessage('Connected, but the recipient cannot receive folders. The prepared selection remains available; remove folders or use another device.');
        } else if (sources.length || directories.length) {
          try {
            const batchId = await manager.offer(sources, directories);
            preparedHostBatchRef.current = batchId;
            updatePreparedHostOfferStatus('offered');
            selectedSourcesRef.current = [];
            selectedDirectoriesRef.current = [];
            setSelectedSources([]);
            setSelectedDirectories([]);
            setMessage(`Direct connection open. Prepared ${sources.length} file${sources.length === 1 ? '' : 's'} offered automatically; the recipient can choose a destination now.`);
          } catch (value) {
            updatePreparedHostOfferStatus('idle');
            preparedHostBatchRef.current = undefined;
            void releasePreparationWakeLock();
            setMessage(`Connected, but the prepared offer could not be sent: ${friendlyError(value)}`);
          }
        }
      }
      const restored = await manager.restoreOutgoing();
      if (restored) setMessage(`Direct connection restored. Re-offered ${restored} unfinished transfer${restored === 1 ? '' : 's'}.`);
      if (contact) {
        for (const prepared of preparedForMeetingRef.current) {
          preparedBatchRef.current.set(prepared.parcel.id, prepared.parcel.id);
          if (!manager.hasOutgoing(prepared.parcel.id)) {
            await manager.offer(prepared.sources, prepared.parcel.directories, prepared.parcel.id);
          }
        }
        if (preparedForMeetingRef.current.length) setMessage(`Connected to ${contact.name}. Prepared parcel${preparedForMeetingRef.current.length === 1 ? '' : 's'} offered automatically.`);
        preparedForMeetingRef.current = [];
        await refreshLocalData();
      }
    } catch (value) {
      fail(value);
    }
  }

  function appendSelection(selection: SourceSelection) {
    const merged = mergeSelections({ sources: selectedSources, directories: selectedDirectories }, selection);
    selectionChanged();
    setSelectedSources(merged.sources);
    setSelectedDirectories(merged.directories);
  }

  async function chooseFiles() {
    try {
      if (!capabilities.filePicker) {
        fileInputRef.current?.click();
        return;
      }
      const handles = await window.showOpenFilePicker({ multiple: true });
      const selection = await selectionFromHandles(handles);
      appendSelection(selection);
    } catch (value) {
      if (!(value instanceof DOMException && value.name === 'AbortError')) fail(value);
    }
  }

  function handleFallbackFiles(event: React.ChangeEvent<HTMLInputElement>) {
    try {
      if (event.target.files?.length) appendSelection(selectionFromFileList(event.target.files));
      event.target.value = '';
    } catch (value) {
      fail(value);
    }
  }

  async function chooseFolder() {
    try {
      if (!capabilities.canSendFolders) throw new Error('Folder selection is not supported on this device. Choose individual files instead.');
      const handle = await window.showDirectoryPicker({ mode: 'read' });
      const selection = await selectionFromHandles([handle]);
      appendSelection(selection);
    } catch (value) {
      if (!(value instanceof DOMException && value.name === 'AbortError')) fail(value);
    }
  }

  async function handleDrop(event: React.DragEvent) {
    event.preventDefault();
    try {
      const selection = await selectionFromDrop(event.dataTransfer);
      appendSelection(selection);
    } catch (value) {
      fail(value);
    }
  }

  async function sendSelected() {
    try {
      if (!transferRef.current) throw new Error('The direct connection is not ready.');
      if (!remoteCapabilities) throw new Error('Still checking what the other device can receive. Try again in a moment.');
      if (!remoteCapabilities.receiveFiles) throw new Error('The other device is in send-only mode and cannot receive files.');
      if (selectedDirectories.length && !remoteCapabilities.receiveFolders) throw new Error('The other device cannot receive folders. Offer individual files instead.');
      await transferRef.current.offer(selectedSources, selectedDirectories);
      setMessage(`Offered ${selectedSources.length} file${selectedSources.length === 1 ? '' : 's'} to the other person.`);
      setSelectedSources([]);
      setSelectedDirectories([]);
    } catch (value) {
      fail(value);
    }
  }

  async function acceptOffer(offer: IncomingOffer, fileIds: string[], conflictPolicy: ConflictPolicy) {
    try {
      if (!capabilities.canReceive) throw new Error('This device is in send-only mode. Open the invitation on Chrome Android or desktop Chrome/Edge to receive files.');
      const destination = await window.showDirectoryPicker({ mode: 'readwrite' });
      await transferRef.current?.accept(offer.id, destination, { fileIds, conflictPolicy });
      setMessage('Offer accepted. Waiting for its turn in the transfer queue.');
    } catch (value) {
      if (!(value instanceof DOMException && value.name === 'AbortError')) fail(value);
    }
  }

  function startPreparing(contact: StoredContact) {
    if (!capabilities.filePicker) {
      setMessage('Prepared parcels require reusable file access in Chrome or Edge. You can still send files after connecting.');
      return;
    }
    setPreparingContact(contact);
    setSelectedSources([]);
    setSelectedDirectories([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function storePreparedParcel() {
    if (!preparingContact) return;
    try {
      const parcel = createPreparedParcel(preparingContact.id, selectedSources, selectedDirectories);
      await savePreparedParcel(parcel);
      void requestPersistentLocalStorage();
      setPreparingContact(null);
      setSelectedSources([]);
      setSelectedDirectories([]);
      setMessage(`Parcel prepared locally for ${preparingContact.name}.`);
      await refreshLocalData();
    } catch (value) {
      setMessage(friendlyError(value));
    }
  }

  async function readyToReceiveOnce(contact: StoredContact) {
    try {
      if (!capabilities.canReceive) throw new Error('This device cannot choose a destination folder and is send-only.');
      const destinationHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
      if (!await ensureWritePermission(destinationHandle)) throw new Error('Write access to the destination was not granted.');
      await testDestinationWrite(destinationHandle);
      const updated: StoredContact = {
        ...contact,
        oneTimeReceive: { destinationHandle, conflictPolicy: 'keep-both', expiresAt: Date.now() + 30 * 60_000 },
        updatedAt: Date.now(),
      };
      await saveContact(updated);
      await refreshLocalData();
      setMessage(`Ready to receive one prepared parcel from ${contact.name}.`);
      await meetContact(updated);
    } catch (value) {
      if (!(value instanceof DOMException && value.name === 'AbortError')) fail(value);
    }
  }

  async function forgetContact(contact: StoredContact) {
    if (!window.confirm(`Forget ${contact.name} and delete parcels prepared for this contact from PairFerry? No source files will be deleted.`)) return;
    const parcels = preparedParcels.filter((parcel) => parcel.contactId === contact.id);
    await Promise.all([deleteContact(contact.id), ...parcels.map((parcel) => deletePreparedParcel(parcel.id))]);
    await refreshLocalData();
  }

  async function renameContact(contact: StoredContact, requestedName: string) {
    const name = requestedName.trim();
    if (!name || name.length > 40) {
      setMessage('Contact names must contain 1 to 40 characters.');
      return;
    }
    try {
      const updated = { ...contact, name, updatedAt: Date.now() };
      await saveContact(updated);
      if (activeContactRef.current?.id === contact.id) activeContactRef.current = updated;
      setMessage(`${contact.name} was renamed to ${name} on this browser.`);
      await refreshLocalData();
    } catch (value) {
      setMessage(friendlyError(value));
    }
  }

  async function removePreparedParcel(parcel: StoredPreparedParcel) {
    await deletePreparedParcel(parcel.id);
    await refreshLocalData();
  }

  async function clearLocalHistory() {
    if (!window.confirm('Clear the privacy-safe completed transfer history from this browser?')) return;
    await clearTransferHistory();
    await refreshLocalData();
  }

  function proposeTrustedContact() {
    const name = contactNameDraft.trim();
    if (!name) {
      setMessage('Enter the name you want to use for this contact.');
      return;
    }
    contactProtocolRef.current?.propose(name);
    setSaveContactOpen(false);
    setContactNameDraft('');
    setMessage('Trusted-contact request sent. Both people must approve before it is saved.');
  }

  async function acceptTrustedContact() {
    if (!incomingContactProposal || !contactNameDraft.trim() || !contactProtocolRef.current) return;
    try {
      await contactProtocolRef.current.accept(incomingContactProposal, contactNameDraft);
      setIncomingContactProposal(null);
      setContactNameDraft('');
    } catch (value) {
      setMessage(friendlyError(value));
    }
  }

  async function runConnectionSpeedTest() {
    const manager = transferRef.current;
    if (!manager) return;
    setSpeedTesting(true);
    try {
      const result = await manager.runSpeedTest();
      setSpeedTest(result);
      setMessage(`Direct-path test: ${formatBytes(result.bytesPerSecond)}/s.`);
    } catch (value) {
      setMessage(friendlyError(value));
    } finally {
      setSpeedTesting(false);
    }
  }

  async function copyLink() {
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  async function installApp() {
    if (!installPrompt) {
      setInstallHelpOpen(true);
      return;
    }
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    if (choice.outcome === 'accepted') setMessage(`Installing ${APP_NAME}…`);
  }

  function applyPwaUpdate() {
    if (!activatePwaUpdate()) window.location.reload();
    setUpdateAvailable(false);
  }

  function removeSelectedTopLevel(name: string) {
    const remaining = removeTopLevel({ sources: selectedSources, directories: selectedDirectories }, name);
    selectionChanged();
    setSelectedSources(remaining.sources);
    setSelectedDirectories(remaining.directories);
  }

  async function toggleCompletionAlerts() {
    const enabled = !completionAlerts;
    if (enabled) {
      audioContextRef.current ??= new AudioContext();
      await audioContextRef.current.resume().catch(() => undefined);
      if ('Notification' in window && Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission === 'denied') setMessage('Desktop notifications were blocked. Completion sound is still enabled.');
      }
    }
    setCompletionAlerts(enabled);
    try { localStorage.setItem('pairferry:completion-alerts', enabled ? 'on' : 'off'); } catch { /* Preference remains active for this page. */ }
  }

  const playCompletionTone = useCallback(() => {
    const context = audioContextRef.current;
    if (!context || context.state !== 'running') return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.setValueAtTime(660, context.currentTime);
    oscillator.frequency.setValueAtTime(880, context.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.28);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.3);
  }, []);

  const announceCompletion = useCallback(async (item: TransferProgress) => {
    playCompletionTone();
    if (document.visibilityState !== 'hidden' || !('Notification' in window) || Notification.permission !== 'granted') return;
    const body = `${item.fileCount} file${item.fileCount === 1 ? '' : 's'} ${item.direction === 'sending' ? 'sent' : 'received'} and verified.`;
    const registration = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
    if (registration) await registration.showNotification('Transfer verified', { body, icon: '/icon.svg', tag: `transfer-${item.batchId}` });
    else new Notification('Transfer verified', { body, icon: '/icon.svg', tag: `transfer-${item.batchId}` });
  }, [playCompletionTone]);

  useEffect(() => {
    for (const item of progress) {
      if (item.state !== 'complete' || announcedCompletionsRef.current.has(item.batchId)) continue;
      announcedCompletionsRef.current.add(item.batchId);
      const contact = activeContactRef.current;
      void saveTransferHistory({
        id: item.batchId,
        contactId: contact?.id,
        peerLabel: contact?.name ?? 'private invitation',
        direction: item.direction,
        fileCount: item.fileCount,
        totalBytes: item.totalBytes,
        completedAt: item.completedAt ?? Date.now(),
        verified: true,
      });
      if (completionAlerts) void announceCompletion(item);
    }
  }, [announceCompletion, completionAlerts, progress]);

  async function copyReceipt(item: TransferProgress) {
    await navigator.clipboard.writeText(buildTransferReceipt(item));
    setMessage('Privacy-safe verified transfer receipt copied.');
  }

  async function resumeSession(session: StoredSession) {
    const invite = { roomId: session.roomId, secret: session.secret };
    if (session.role === 'host') await startHost(invite);
    else {
      history.replaceState({}, '', `/room/${invite.roomId}#${invite.secret}`);
      setActiveInvite(invite);
      setStage('join');
    }
  }

  async function removeRecent(session: StoredSession) {
    const transfers = (await listTransfers()).filter((transfer) => transfer.roomId === session.roomId);
    await Promise.all([deleteSession(session.id), ...transfers.map((transfer) => deleteTransfer(transfer.id))]);
    await refreshLocalData();
  }

  async function closeRoom() {
    if (hasActiveTransfer && !window.confirm('A transfer is still active. Close the room and delete its local recovery record?')) return;
    conflictRequest?.resolve('skip-existing');
    setConflictRequest(null);
    await releasePreparationWakeLock();
    updatePreparedHostOfferStatus('idle');
    preparedHostBatchRef.current = undefined;
    peerRef.current?.close();
    gameProtocolRef.current?.close();
    contactProtocolRef.current?.close();
    await signalingRef.current?.close();
    peerRef.current = undefined;
    signalingRef.current = undefined;
    transferRef.current = undefined;
    contactProtocolRef.current = undefined;
    gameProtocolRef.current = undefined;
    if (activeInvite) {
      const transfers = (await listTransfers()).filter((transfer) => transfer.roomId === activeInvite.roomId);
      await Promise.all([deleteSession(activeInvite.roomId), ...transfers.map((transfer) => deleteTransfer(transfer.id))]).catch(() => undefined);
    }
    history.replaceState({}, '', '/');
    setStage('idle');
    setActiveInvite(null);
    activeContactRef.current = null;
    setActiveContact(null);
    preparedForMeetingRef.current = [];
    preparedBatchRef.current.clear();
    setOffers([]);
    setProgress([]);
    setRemoteCapabilities(null);
    selectedSourcesRef.current = [];
    selectedDirectoriesRef.current = [];
    setSelectedSources([]);
    setSelectedDirectories([]);
    setQueue({ batchIds: [] });
    setSpeedTest(null);
    setGameLounge(emptyGameLounge);
    setMessage('');
    await refreshLocalData();
  }

  const selectedBytes = selectedSources.reduce((total, source) => total + source.manifest.size, 0);
  const activeProgress = progress.find((item) => item.state === 'active' || item.state === 'paused' || item.state === 'failed');
  const quality = connectionSummary(diagnostics);
  const canOfferSelected = remoteCapabilities?.receiveFiles === true
    && (!selectedDirectories.length || remoteCapabilities.receiveFolders);
  const localModeLabel = capabilities.tier === 'full' ? 'Full send & receive' : capabilities.tier === 'send-only' ? 'Send-only mode' : 'Unsupported';
  const selectedGroups = useMemo(() => {
    const groups = new Map<string, { name: string; fileCount: number; bytes: number; folder: boolean }>();
    for (const source of selectedSources) {
      const name = source.manifest.relativePath.split('/')[0];
      const group = groups.get(name) ?? { name, fileCount: 0, bytes: 0, folder: source.manifest.relativePath.includes('/') };
      group.fileCount += 1;
      group.bytes += source.manifest.size;
      group.folder ||= source.manifest.relativePath.includes('/');
      groups.set(name, group);
    }
    for (const directory of selectedDirectories) {
      const name = directory.split('/')[0];
      const group = groups.get(name) ?? { name, fileCount: 0, bytes: 0, folder: true };
      group.folder = true;
      groups.set(name, group);
    }
    return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedDirectories, selectedSources]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label={`${APP_NAME} home`}>
          <img className="brand-logo" src="/icon.svg" alt="" />
          <span className="brand-copy"><strong>Pair<span>Ferry</span></strong><small>Private file transfer</small></span>
        </a>
        <div className="header-actions">
          <a className="coffee-button" href={COFFEE_URL} target="_blank" rel="noopener noreferrer" aria-label="Buy djpokis a coffee (opens in a new tab)"><span aria-hidden="true">☕</span> Buy me a coffee</a>
          <button className="button ghost" onClick={() => setHelpOpen((open) => !open)}>Help</button>
        </div>
      </header>

      <main>
        {stage === 'idle' && (
          <section className="hero card">
            <span className="eyebrow">PRIVATE ROUTE • DIRECT TRANSFER • NO UPLOAD</span>
            <h1>Big files.<br /><em>Straight across.</em></h1>
            <p className="lead">Pair two devices with a private link. PairFerry moves files directly between their browsers—not through our servers.</p>
            {capabilities.tier === 'unsupported' && <div className="alert error">{capabilityDescription(capabilities)}</div>}
            {capabilities.tier === 'send-only' && <div className="alert info"><strong>Send-only compatibility mode.</strong> {capabilityDescription(capabilities)}</div>}
            {capabilities.android && capabilities.tier === 'full' && <div className="alert success"><strong>Android ready.</strong> Keep this app open and visible until every file is verified.</div>}
            {configProblems.length > 0 && <div className="alert warning"><strong>Setup required.</strong> The owner must complete <code>docs/FIREBASE_SETUP.md</code>.</div>}
            <button className="button primary large" disabled={!capabilities.canConnect || configProblems.length > 0} onClick={() => void startHost()}>
              Open a private route <span aria-hidden="true">→</span>
            </button>
            <div className="trust-row">
              <span>✓ No file-size quota</span><span>✓ Direct device route</span><span>✓ Android supported</span><span>✓ No account required</span>
            </div>
            <p className="support-note">Useful and free? <a href={COFFEE_URL} target="_blank" rel="noopener noreferrer">Buy me a coffee and support the project ☕</a></p>
          </section>
        )}

        {stage === 'idle' && !standalone && capabilities.canConnect && (
          <section className="pwa-card card" aria-labelledby="install-title">
            <span className="pwa-icon" aria-hidden="true">↧</span>
            <span><strong id="install-title">Install PairFerry</strong><small>Keep a private file route on your home screen or desktop. Installation is free.</small></span>
            <button className="button secondary" onClick={() => void installApp()}>{installPrompt ? 'Install app' : 'How to install'}</button>
          </section>
        )}

        {stage === 'idle' && preparingContact && (
          <section className="narrow card prepare-card" aria-labelledby="prepare-title">
            <span className="eyebrow">PREPARED PARCEL</span>
            <h2 id="prepare-title">Prepare files for {preparingContact.name}</h2>
            <p>The files remain on this device. PairFerry stores reusable handles and validates every source again before meeting.</p>
            <input ref={fileInputRef} className="file-input-fallback" type="file" multiple onChange={handleFallbackFiles} />
            <div className="button-row"><button className="button secondary" onClick={() => void chooseFiles()}>Add files</button>{capabilities.canSendFolders && <button className="button secondary" onClick={() => void chooseFolder()}>Add folder</button>}</div>
            {(selectedSources.length > 0 || selectedDirectories.length > 0) && <div className="selection-summary">
              <div><strong>{selectedSources.length} file{selectedSources.length === 1 ? '' : 's'} ready</strong><span>{formatBytes(selectedBytes)}</span></div>
              <ul>{selectedGroups.map((group) => <li key={group.name}><span className="selection-name"><strong>{group.name}</strong><small>{group.folder ? `${group.fileCount} file${group.fileCount === 1 ? '' : 's'}` : formatBytes(group.bytes)}</small></span><button className="remove-selection" onClick={() => removeSelectedTopLevel(group.name)}>Remove</button></li>)}</ul>
            </div>}
            <div className="button-row"><button className="button primary" disabled={!selectedSources.length && !selectedDirectories.length} onClick={() => void storePreparedParcel()}>Save prepared parcel</button><button className="button ghost" onClick={() => { setPreparingContact(null); setSelectedSources([]); setSelectedDirectories([]); }}>Cancel</button></div>
          </section>
        )}

        {stage === 'join' && (
          <form className="narrow card" data-testid="join-form" onSubmit={(event) => {
            event.preventDefault();
            if (capabilities.canConnect) void joinRoom();
          }}>
            <span className="eyebrow">PRIVATE INVITATION</span>
            <h1>Join this transfer</h1>
            <p>The person who sent this link must keep their page open and approve you.</p>
            <label className="field">Your name <span>(optional)</span>
              <input ref={displayNameInputRef} value={displayName} maxLength={40} onChange={(event) => setDisplayName(event.target.value)} placeholder="Shown only to the sender" autoComplete="name" enterKeyHint="go" autoFocus />
            </label>
            {capabilities.tier === 'send-only' && <div className="alert info">{capabilityDescription(capabilities)}</div>}
            <button className="button primary large" type="submit" disabled={!capabilities.canConnect}>Request to connect</button>
            <p className="microcopy">Only accept transfer links from someone you trust. A direct connection can reveal your public IP address to the other person.</p>
          </form>
        )}

        {(stage === 'hosting' || stage === 'waiting' || stage === 'contacting' || stage === 'connecting') && (
          <>
          <section className="narrow card connection-card">
            <div className="pulse" aria-hidden="true"><span /></div>
            <span className="eyebrow">{stage === 'hosting' ? 'ROOM READY' : stage === 'contacting' ? 'TRUSTED CONTACT' : 'CONNECTING'}</span>
            <h1>{stage === 'hosting' ? 'Invite the other device' : stage === 'contacting' ? `Waiting for ${activeContact?.name ?? 'contact'}` : 'Opening a direct route'}</h1>
            <p>{message}</p>
            {stage === 'hosting' && (
              <>
                <InvitationTools inviteLink={inviteLink} copied={copied} onCopy={copyLink} onNotice={setMessage} />
                <button className="button secondary" onClick={() => void regenerateInvitation()}>Revoke & create new link</button>
                <p className="microcopy">Creating a new link immediately invalidates this one.</p>
                {joinRequests.map((request) => (
                  <div className="join-request" key={request.uid}><span><strong>{request.displayName}</strong> wants to connect</span><button className="button primary" onClick={() => void approve(request)}>Approve</button></div>
                ))}
              </>
            )}
            {stage === 'contacting' && <div className="alert info"><strong>No permanent presence.</strong> PairFerry waits only for this deliberate meeting and disconnects Firebase as soon as the direct route opens.</div>}
            <button className="button ghost" onClick={() => void closeRoom()}>Cancel</button>
          </section>
          {stage === 'hosting' && <section className="card host-preparation" aria-labelledby="host-preparation-title">
            <span className="eyebrow">PREPARE WHILE WAITING</span>
            <h2 id="host-preparation-title">Queue the first file offer</h2>
            <p>Choose files now, review them, then queue the offer. After you approve the recipient and the direct route opens, PairFerry offers this selection automatically. The recipient still chooses a destination and accepts before any file bytes move.</p>
            <div className="drop-zone" onDragOver={(event) => event.preventDefault()} onDrop={(event) => void handleDrop(event)}>
              <input ref={fileInputRef} className="file-input-fallback" type="file" multiple onChange={handleFallbackFiles} />
              <div className="drop-icon">⇢</div><strong>{capabilities.mobile ? 'Choose files from this device' : 'Drop files or folders here'}</strong><span>Nothing is uploaded while you wait.</span>
              <div className="button-row"><button className="button secondary" onClick={() => void chooseFiles()}>Add files</button>{capabilities.canSendFolders && <button className="button secondary" onClick={() => void chooseFolder()}>Add folder</button>}</div>
            </div>
            {(selectedSources.length > 0 || selectedDirectories.length > 0) && <div className="selection-summary">
              <div><strong>{selectedSources.length} file{selectedSources.length === 1 ? '' : 's'} in {selectedDirectories.length} folder{selectedDirectories.length === 1 ? '' : 's'} prepared</strong><span>{formatBytes(selectedBytes)}</span></div>
              <ul>{selectedGroups.map((group) => <li key={group.name}><span className="selection-name"><strong>{group.name}</strong><small>{group.folder ? `${group.fileCount} file${group.fileCount === 1 ? '' : 's'}` : formatBytes(group.bytes)}</small></span><button className="remove-selection" aria-label={`Remove ${group.name} from prepared selection`} onClick={() => removeSelectedTopLevel(group.name)}>Remove</button></li>)}</ul>
              {preparedHostOfferStatus === 'queued' && <div className="alert success" role="status"><strong>Automatic offer queued.</strong> Approve the expected recipient when they appear. Keep this page open and the computer awake.</div>}
              <div className="button-row">
                <button className="button primary" disabled={preparedHostOfferStatus === 'queued'} onClick={() => void queuePreparedHostOffer()}>{preparedHostOfferStatus === 'queued' ? 'Queued for connection' : 'Queue automatic offer'}</button>
                <button className="button ghost" onClick={clearSelection}>Clear</button>
              </div>
            </div>}
            <p className="microcopy">Once queued, PairFerry requests a screen wake lock. The browser must remain open and visible, and the computer must stay powered on and online.</p>
          </section>}
          </>
        )}

        {stage === 'connected' && (
          <section className="workspace">
            <div className="connection-banner">
              <span className="live-dot" />
              <span className="connection-copy"><strong>Direct route open</strong><small>Firebase is disconnected. Files cross only to the other device.</small></span>
              <span className={`mode-chip ${capabilities.tier}`}><strong>{localModeLabel}</strong><small>{remoteCapabilities ? (remoteCapabilities.receiveFiles ? 'Peer can receive' : 'Peer is send-only') : 'Checking peer…'}</small></span>
              <span className={`quality-chip ${quality.tone}`} title="Measured from the active WebRTC path"><strong>{quality.label}</strong><small>{quality.detail}</small></span>
              <button className={`button connection-action ${completionAlerts ? 'secondary' : 'ghost'}`} aria-pressed={completionAlerts} onClick={() => void toggleCompletionAlerts()}>{completionAlerts ? 'Alerts on' : 'Completion alerts'}</button>
              <button className="button ghost compact" onClick={() => void closeRoom()}>Close room</button>
            </div>
            {capabilities.mobile && <div className="mobile-warning" role="status"><strong>Keep this app visible during transfers.</strong><span>Phones may pause web apps in the background. We checkpoint and pause safely when the app is hidden, then resume when you return.{!capabilities.canResumeOutgoing ? ' Files chosen on this device must be selected again after a full reload.' : ''}</span></div>}
            <div className="connected-tools card">
              <span><strong>{activeContact ? `Trusted contact: ${activeContact.name}` : 'Useful connection tools'}</strong><small>{speedTest ? `Last direct-path test: ${formatBytes(speedTest.bytesPerSecond)}/s` : 'Test the direct route or mutually save this device for another deliberate meeting.'}</small></span>
              <div className="button-row">
                <button className="button secondary" disabled={hasActiveTransfer || speedTesting} onClick={() => void runConnectionSpeedTest()}>{speedTesting ? 'Testing…' : 'Test connection speed'}</button>
                {!activeContact && <button className="button secondary" onClick={() => { setSaveContactOpen(true); setContactNameDraft(''); }}>Save trusted contact</button>}
              </div>
            </div>
            {saveContactOpen && <div className="inline-contact-form card"><label className="field">Name for this device<input value={contactNameDraft} maxLength={40} autoFocus placeholder="For example, Alex or Office PC" onChange={(event) => setContactNameDraft(event.target.value)} /></label><div className="button-row"><button className="button primary" onClick={proposeTrustedContact}>Ask both devices to save</button><button className="button ghost" onClick={() => setSaveContactOpen(false)}>Cancel</button></div><p className="microcopy">The name stays only in this browser. The other person chooses their own local name.</p></div>}
            <Suspense fallback={<section className="card game-lounge"><div className="game-lounge-heading"><span className="game-lounge-mark" aria-hidden="true">4</span><span><strong>Passenger Lounge</strong><small>Preparing optional games…</small></span></div></section>}>
              <GameLounge
                snapshot={gameLounge}
                localRole={roleRef.current}
                peerName={activeContact?.name ?? 'The other person'}
                onPropose={(kind) => gameProtocolRef.current?.propose(kind)}
                onAccept={(proposalId) => gameProtocolRef.current?.accept(proposalId)}
                onDecline={(proposalId) => gameProtocolRef.current?.decline(proposalId)}
                onPlay={(move) => gameProtocolRef.current?.play(move) ?? Promise.reject(new Error('The game connection is not ready.'))}
                onChooseSignal={(choice) => gameProtocolRef.current?.chooseSignal(choice) ?? Promise.reject(new Error('The game connection is not ready.'))}
                onContinueSignal={() => gameProtocolRef.current?.continueSignal() ?? Promise.reject(new Error('The game connection is not ready.'))}
                onLeave={() => gameProtocolRef.current?.leave()}
                onRematch={() => gameProtocolRef.current?.rematch()}
              />
            </Suspense>
            <div className="workspace-grid">
              <div className="card send-card">
                <span className="eyebrow">SEND</span>
                <h2>Choose files for the crossing</h2>
                <div className="drop-zone" onDragOver={(event) => event.preventDefault()} onDrop={(event) => void handleDrop(event)}>
                  <input ref={fileInputRef} className="file-input-fallback" type="file" multiple onChange={handleFallbackFiles} />
                  <div className="drop-icon">⇢</div><strong>{capabilities.mobile ? 'Choose files from this device' : 'Drop files or folders here'}</strong><span>They stay docked on this device until the recipient accepts.</span>
                  <div className="button-row"><button className="button secondary" onClick={() => void chooseFiles()}>Add files</button>{capabilities.canSendFolders && <button className="button secondary" onClick={() => void chooseFolder()}>Add folder</button>}</div>
                </div>
                {!remoteCapabilities && <p className="peer-compatibility">Checking what the other device can receive…</p>}
                {remoteCapabilities && !remoteCapabilities.receiveFiles && <div className="alert warning peer-compatibility"><strong>The other device is send-only.</strong> It can send files to you, but cannot accept this offer.</div>}
                {(selectedSources.length > 0 || selectedDirectories.length > 0) && (
                  <div className="selection-summary">
                    <div><strong>{selectedSources.length} file{selectedSources.length === 1 ? '' : 's'} in {selectedDirectories.length} folder{selectedDirectories.length === 1 ? '' : 's'} ready</strong><span>{formatBytes(selectedBytes)}</span></div>
                    <ul>{selectedGroups.map((group) => <li key={group.name}><span className="selection-name"><strong>{group.name}</strong><small>{group.folder ? `${group.fileCount} file${group.fileCount === 1 ? '' : 's'}` : formatBytes(group.bytes)}</small></span><button className="remove-selection" aria-label={`Remove ${group.name} from selection`} onClick={() => removeSelectedTopLevel(group.name)}>Remove</button></li>)}</ul>
                    <div className="button-row"><button className="button primary" disabled={!canOfferSelected} onClick={() => void sendSelected()}>Offer files</button><button className="button ghost" onClick={() => { setSelectedSources([]); setSelectedDirectories([]); }}>Clear</button></div>
                  </div>
                )}
              </div>

              <div className="card activity-card">
                <span className="eyebrow">ACTIVITY</span>
                <h2>Transfers</h2>
                {!offers.length && !progress.length && <div className="empty-state"><span>↔</span><p>File offers and transfer progress will appear here.</p></div>}
                {offers.filter((offer) => offer.status === 'offered').map((offer) => <OfferCard key={offer.id} offer={offer} canReceive={capabilities.canReceive} onAccept={(incoming, fileIds, policy) => void acceptOffer(incoming, fileIds, policy)} onDecline={(incoming) => transferRef.current?.decline(incoming.id)} />)}
                {progress.map((item) => <ProgressCard key={item.batchId} item={item} manager={transferRef.current} onCopyReceipt={(completed) => void copyReceipt(completed)} />)}
              </div>
            </div>
            {queue.batchIds.length > 1 && <section className="card queue-card" aria-labelledby="queue-title"><span className="eyebrow">SHARED QUEUE</span><h2 id="queue-title">Transfer order</h2><p>The host coordinates one batch at a time. Move waiting batches without interrupting the active one.</p><ol>{queue.batchIds.map((batchId, index) => { const item = progress.find((entry) => entry.batchId === batchId); return <li key={batchId} className={queue.activeBatchId === batchId ? 'active' : ''}><span><strong>{queue.activeBatchId === batchId ? 'Active' : `Waiting ${index + 1}`}</strong><small>{item ? `${item.direction === 'sending' ? 'Sending' : 'Receiving'} · ${item.fileCount} files · ${formatBytes(item.totalBytes)}` : 'Accepted batch'}</small></span>{queue.activeBatchId !== batchId && <span className="queue-buttons"><button className="button ghost compact" aria-label="Move batch up" onClick={() => transferRef.current?.moveQueued(batchId, 'up')}>↑</button><button className="button ghost compact" aria-label="Move batch down" onClick={() => transferRef.current?.moveQueued(batchId, 'down')}>↓</button></span>}</li>; })}</ol></section>}
            {message && <div className="toast" role="status">{message}</div>}
          </section>
        )}

        {stage === 'error' && (
          <section className="narrow card error-card">
            <span className="error-symbol">!</span><span className="eyebrow">CONNECTION PROBLEM</span><h1>We could not continue</h1>
            <p>{error}</p>
            <div className="button-row"><button className="button primary" onClick={() => window.location.reload()}>Try again</button><button className="button secondary" onClick={() => setHelpOpen(true)}>Troubleshoot</button></div>
          </section>
        )}

        {stage === 'idle' && recentSessions.length > 0 && (
          <section className="recent card"><span className="eyebrow">RECOVERY</span><h2>Unfinished transfers</h2>
            {recentSessions.map((session) => <div className="recent-row" key={session.id}><span><strong>{session.role === 'host' ? 'Transfer you created' : 'Transfer you joined'}</strong><small>{new Date(session.updatedAt).toLocaleString()}</small></span><div><button className="button secondary" onClick={() => void resumeSession(session)}>Resume</button><button className="button ghost" onClick={() => void removeRecent(session)}>Forget</button></div></div>)}
          </section>
        )}

        {stage === 'idle' && <ContactSections
          contacts={contacts}
          parcels={preparedParcels}
          history={transferHistory}
          onMeet={(contact) => void meetContact(contact)}
          onPrepare={startPreparing}
          onReadyReceive={(contact) => void readyToReceiveOnce(contact)}
          onRename={(contact, name) => void renameContact(contact, name)}
          onForget={(contact) => void forgetContact(contact)}
          onDeleteParcel={(parcel) => void removePreparedParcel(parcel)}
          onClearHistory={() => void clearLocalHistory()}
        />}

        {stage === 'idle' && <ProductInfo />}
        {stage === 'idle' && message && <div className="toast" role="status">{message}</div>}

        {incomingContactProposal && <div className="modal-backdrop" role="presentation">
          <section className="decision-modal card" role="dialog" aria-modal="true" aria-labelledby="contact-request-title">
            <span className="eyebrow">MUTUAL APPROVAL</span><h2 id="contact-request-title">Save this trusted contact?</h2>
            <p>The other person wants both browsers to remember each other locally. Nothing is added to an account or uploaded as a contact.</p>
            <label className="field">Your name for this device<input value={contactNameDraft} maxLength={40} autoFocus placeholder="For example, Alex or Home PC" onChange={(event) => setContactNameDraft(event.target.value)} /></label>
            <div className="button-row"><button className="button primary" disabled={!contactNameDraft.trim()} onClick={() => void acceptTrustedContact()}>Save on this device</button><button className="button ghost" onClick={() => { contactProtocolRef.current?.decline(incomingContactProposal); setIncomingContactProposal(null); }}>Decline</button></div>
          </section>
        </div>}

        {conflictRequest && <div className="modal-backdrop" role="presentation">
          <section className="decision-modal card" role="dialog" aria-modal="true" aria-labelledby="conflict-title">
            <span className="eyebrow">FILE ALREADY EXISTS</span><h2 id="conflict-title">Choose what happens</h2><p className="conflict-path">{conflictRequest.relativePath}</p>
            <div className="button-row"><button className="button primary" onClick={() => { conflictRequest.resolve('keep-both'); setConflictRequest(null); }}>Keep both</button><button className="button secondary" onClick={() => { conflictRequest.resolve('replace'); setConflictRequest(null); }}>Replace</button><button className="button ghost" onClick={() => { conflictRequest.resolve('skip-existing'); setConflictRequest(null); }}>Skip incoming</button></div>
          </section>
        </div>}

        {helpOpen && <HelpPanel
          capabilities={capabilities}
          remoteCapabilities={remoteCapabilities}
          onClose={() => setHelpOpen(false)}
          onCopy={() => void copyDiagnostics({
            connectionState: diagnostics.connectionState,
            iceState: diagnostics.iceState,
            signalingState: diagnostics.signalingState,
            capabilityTier: capabilities.tier,
            directoryPicker: capabilities.directoryPicker,
            filePicker: capabilities.filePicker,
            peerCanReceive: remoteCapabilities?.receiveFiles,
            candidateType: diagnostics.candidateType,
            protocol: diagnostics.protocol,
            roundTripTimeMs: diagnostics.roundTripTimeMs,
            lastErrorCode: error ? 'SESSION_ERROR' : undefined,
          })}
        />}
        {installHelpOpen && <InstallPanel capabilities={capabilities} onClose={() => setInstallHelpOpen(false)} />}
      </main>
      <footer><span>PairFerry never stores your files.</span><a className="footer-link" href={COFFEE_URL} target="_blank" rel="noopener noreferrer">☕ Support this free project</a><button className="footer-link" onClick={() => setHelpOpen(true)}>Privacy & help</button><span>v{APP_VERSION}</span></footer>
      {activeProgress?.state === 'failed' && <div className="toast error" role="alert">{activeProgress.error}</div>}
      {updateAvailable && <div className="update-banner" role="status"><span><strong>A new version is ready.</strong><small>Update when no transfer is active.</small></span><button className="button primary" disabled={hasActiveTransfer} onClick={applyPwaUpdate}>Update now</button></div>}
    </div>
  );
}

function ProgressCard({ item, manager, onCopyReceipt }: { item: TransferProgress; manager?: TransferManager; onCopyReceipt: (item: TransferProgress) => void }) {
  const percent = item.totalBytes ? Math.min(100, (item.transferredBytes / item.totalBytes) * 100) : item.state === 'complete' ? 100 : 0;
  const eta = item.bytesPerSecond > 0 ? (item.totalBytes - item.transferredBytes) / item.bytesPerSecond : Number.NaN;
  return <article className="progress-card">
    <div className="progress-heading"><span><strong>{item.direction === 'sending' ? 'Sending' : 'Receiving'} {item.fileCount} file{item.fileCount === 1 ? '' : 's'}</strong><small>{item.state === 'complete' ? 'Verified complete' : item.state}</small></span><strong>{percent.toFixed(0)}%</strong></div>
    <div className="progress-track"><span style={{ width: `${percent}%` }} /></div>
    <div className="progress-meta"><span>{formatBytes(item.transferredBytes)} / {formatBytes(item.totalBytes)}</span><span>{formatBytes(item.bytesPerSecond)}/s</span><span>{formatDuration(eta)}</span></div>
    {item.currentFile && <p className="current-file">{item.currentFile}</p>}
    {(item.state === 'active' || item.state === 'paused') && <div className="button-row">
      {item.state === 'paused' ? <button className="button secondary" onClick={() => manager?.resume(item.batchId)}>Resume</button> : <button className="button secondary" onClick={() => manager?.pause(item.batchId)}>Pause</button>}
      {item.direction === 'receiving' && <button className="button ghost" onClick={() => manager?.skipCurrentFile(item.batchId)}>Skip file</button>}
      <button className="button ghost danger" onClick={() => manager?.cancel(item.batchId)}>Cancel</button>
    </div>}
    {item.state === 'failed' && item.direction === 'sending' && <button className="button secondary" onClick={() => manager?.retry(item.batchId)}>Retry batch</button>}
    {item.state === 'complete' && <div className="verified-actions"><span aria-hidden="true">✓</span><span><strong>Every received block was verified</strong><small>The receipt contains no filenames or invitation details.</small></span><button className="button secondary" onClick={() => onCopyReceipt(item)}>Copy receipt</button></div>}
  </article>;
}

function HelpPanel({ capabilities, remoteCapabilities, onClose, onCopy }: {
  capabilities: DeviceCapabilities;
  remoteCapabilities: TransferCapabilities | null;
  onClose: () => void;
  onCopy: () => void;
}) {
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="help-panel" role="dialog" aria-modal="true" aria-labelledby="help-title">
      <button className="close-button" aria-label="Close help" onClick={onClose}>×</button><span className="eyebrow">HELP</span><h2 id="help-title">Connection checklist</h2>
      <section className="help-status" aria-labelledby="capability-title">
        <h3 id="capability-title">Device capability check</h3>
        <dl>
          <div><dt>This device</dt><dd>{capabilities.canReceive ? 'Send & receive' : capabilities.canConnect ? 'Send-only' : 'Unsupported'}</dd></div>
          <div><dt>Destination-folder access</dt><dd>{capabilities.directoryPicker ? 'Available' : 'Missing'}</dd></div>
          {remoteCapabilities && <div><dt>Other device</dt><dd>{remoteCapabilities.receiveFiles ? 'Can receive' : 'Send-only'}</dd></div>}
        </dl>
      </section>
      {(!capabilities.canReceive || remoteCapabilities?.receiveFiles === false) && <div className="alert warning">
        <strong>Why send-only?</strong> Receiving large files requires the browser’s destination-folder API. On the send-only computer, open the same invitation in the latest <strong>Google Chrome</strong> or <strong>Microsoft Edge</strong> desktop browser in a normal window. Firefox, Safari, Brave’s default configuration, private/embedded browsers, and managed browsers may not expose this API.
      </div>}
      <section className="help-topic" aria-labelledby="privacy-title">
        <h3 id="privacy-title">“requestStorageAccess: Permission denied”</h3>
        <p>This is usually an invisible reCAPTCHA/App Check privacy warning, not a file or folder permission. If the room opens, it can be ignored. If joining also fails with an App Check or reCAPTCHA error:</p>
        <ol><li>Use a normal Chrome or Edge window—not an embedded messenger browser or private window.</li><li>Allow cookies/site data for PairFerry in that browser’s site settings. You do not need to allow third-party cookies globally.</li><li>Temporarily lower strict tracking protection or Shields for the PairFerry site and pause content-blocking extensions there.</li><li>Reload both devices and create a new invitation. If Google or Firebase is blocked by the network, try a phone hotspot.</li></ol>
      </section>
      <section className="help-topic" aria-labelledby="install-console-title">
        <h3 id="install-console-title">Install banner and service-worker warnings</h3>
        <p><code>beforeinstallpromptevent.preventDefault()</code> is expected: PairFerry saves the browser prompt for its own install button. PairFerry v2.2.0 also prevents rejected <code>sw.js</code> fetch promises by returning the cached app shell or a clear offline response.</p>
      </section>
      <ol><li>Both people must keep this app open and visible during transfers.</li><li>For full send and receive, use current Chrome on Android or Chrome/Edge on desktop.</li><li>iPhone and iPad can send selected files, but cannot receive directly into a folder in the current release.</li><li>Disable VPNs and strict privacy proxies temporarily.</li><li>If a work or school network blocks the connection, try a home network or phone hotspot.</li><li>Allow file and destination-folder permissions when asked.</li></ol>
      <div className="alert warning">This free edition intentionally has no relay server. Some restrictive networks cannot make a direct connection.</div>
      <button className="button secondary" onClick={onCopy}>Copy privacy-safe diagnostics</button>
      <p className="support-panel">This project is free to use and has no advertising. If it helps you, <a href={COFFEE_URL} target="_blank" rel="noopener noreferrer">buy me a coffee</a>.</p>
      <p className="microcopy">The repository also includes a complete owner and troubleshooting manual.</p>
    </aside>
  </div>;
}

function InstallPanel({ capabilities, onClose }: { capabilities: DeviceCapabilities; onClose: () => void }) {
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="help-panel install-panel" role="dialog" aria-modal="true" aria-labelledby="install-help-title">
      <button className="close-button" aria-label="Close installation instructions" onClick={onClose}>×</button>
      <span className="eyebrow">INSTALL</span><h2 id="install-help-title">Add it to your device</h2>
      {capabilities.ios ? <>
        <ol><li>Open this page in Safari.</li><li>Tap the <strong>Share</strong> button.</li><li>Choose <strong>Add to Home Screen</strong>, then tap <strong>Add</strong>.</li></ol>
        <div className="alert info"><strong>iPhone and iPad are send-only in this release.</strong> The installed app can send selected files, but Safari cannot grant direct destination-folder access for receiving.</div>
      </> : capabilities.android ? <>
        <ol><li>Open this page in Chrome.</li><li>Tap Chrome’s menu (⋮).</li><li>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li><li>Confirm, then launch it from your home screen.</li></ol>
      </> : <>
        <ol><li>Open this page in Chrome or Edge.</li><li>Use the install icon in the address bar, or open the browser menu.</li><li>Choose <strong>Install PairFerry</strong> and confirm.</li></ol>
      </>}
      <div className="alert warning"><strong>Keep the installed app visible.</strong> Mobile operating systems can suspend background web apps, so active transfers pause when hidden.</div>
      <button className="button secondary" onClick={onClose}>Got it</button>
    </aside>
  </div>;
}

export default App;
